import { useCallback, useState, useRef, DragEvent, ChangeEvent } from 'react';
import { Upload, X, FileText, Image, AlertCircle } from 'lucide-react';

export interface QueuedFile {
  id: string;
  file: File;
  preview?: string;
  pageCount?: number;
  error?: string;
}

interface FileDropzoneProps {
  accept?: string[];
  maxSize?: number; // in bytes
  maxFiles?: number;
  onFilesChange: (files: QueuedFile[]) => void;
  files: QueuedFile[];
  disabled?: boolean;
  className?: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) {
    return <Image className="w-5 h-5 text-blue-500" />;
  }
  return <FileText className="w-5 h-5 text-red-500" />;
};

export function FileDropzone({
  accept = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'],
  maxSize = 25 * 1024 * 1024, // 25MB
  maxFiles = 10,
  onFilesChange,
  files,
  disabled = false,
  className = '',
}: FileDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    if (!accept.includes(file.type)) {
      return `Invalid file type. Accepted: ${accept.map(t => t.split('/')[1]).join(', ')}`;
    }
    if (file.size > maxSize) {
      return `File too large. Maximum: ${formatFileSize(maxSize)}`;
    }
    return null;
  }, [accept, maxSize]);

  const processFiles = useCallback((fileList: FileList | File[]) => {
    const newFiles: QueuedFile[] = [];
    const existingCount = files.length;

    Array.from(fileList).forEach((file, index) => {
      if (existingCount + newFiles.length >= maxFiles) {
        return;
      }

      const error = validateFile(file);
      const queuedFile: QueuedFile = {
        id: `${Date.now()}-${index}-${file.name}`,
        file,
        error: error || undefined,
      };

      // Generate preview for images
      if (file.type.startsWith('image/') && !error) {
        queuedFile.preview = URL.createObjectURL(file);
      }

      // For PDFs, we'd need a library to count pages
      // This is a placeholder - in real app, use pdf.js
      if (file.type === 'application/pdf' && !error) {
        queuedFile.pageCount = undefined; // Would be set after parsing
      }

      newFiles.push(queuedFile);
    });

    onFilesChange([...files, ...newFiles]);
  }, [files, maxFiles, onFilesChange, validateFile]);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      processFiles(droppedFiles);
    }
  }, [disabled, processFiles]);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      processFiles(selectedFiles);
    }
    // Reset input
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [processFiles]);

  const handleRemove = useCallback((id: string) => {
    const file = files.find(f => f.id === id);
    if (file?.preview) {
      URL.revokeObjectURL(file.preview);
    }
    onFilesChange(files.filter(f => f.id !== id));
  }, [files, onFilesChange]);

  const handleClick = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  return (
    <div className={className}>
      {/* Dropzone */}
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
          ${isDragging ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept.join(',')}
          onChange={handleChange}
          disabled={disabled}
          className="hidden"
        />

        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />

        <p className="text-gray-600 font-medium">
          {isDragging ? 'Drop files here' : 'Drop files here or click to browse'}
        </p>

        <p className="text-sm text-gray-500 mt-2">
          {accept.map(t => t.split('/')[1].toUpperCase()).join(', ')} (max {formatFileSize(maxSize)})
        </p>

        {maxFiles > 1 && (
          <p className="text-sm text-gray-500">
            Up to {maxFiles} files • Multi-page PDFs supported
          </p>
        )}
      </div>

      {/* File queue */}
      {files.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Queued Files ({files.length})
          </h4>
          <ul className="space-y-2">
            {files.map((queuedFile) => (
              <li
                key={queuedFile.id}
                className={`
                  flex items-center justify-between p-3 rounded-lg border
                  ${queuedFile.error ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}
                `}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {queuedFile.preview ? (
                    <img
                      src={queuedFile.preview}
                      alt=""
                      className="w-10 h-10 object-cover rounded"
                    />
                  ) : (
                    getFileIcon(queuedFile.file.type)
                  )}

                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {queuedFile.file.name}
                      {queuedFile.pageCount && (
                        <span className="text-gray-500 font-normal"> ({queuedFile.pageCount} pages)</span>
                      )}
                    </p>
                    {queuedFile.error ? (
                      <p className="text-xs text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {queuedFile.error}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500">
                        {formatFileSize(queuedFile.file.size)}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleRemove(queuedFile.id)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  aria-label={`Remove ${queuedFile.file.name}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
